// Form rules and legacy preview fixtures. The live screen uses the
// monitoring/devices/ API via organisationDevicesApi.js.


// Companies of this organisation, with the chip colour used for each.
export const organisationCompanies = [
  { name: "STRCO India", color: "#2563EB" },
  { name: "STRCO Manufacturing", color: "#059669" },
  { name: "STRCO Logistics", color: "#D97706" },
];

export const companyColor = (name) =>
  organisationCompanies.find((company) => company.name === name)?.color;

export const STATUS_COLORS = { Online: "#059669", Offline: "#EF4444", Unknown: "#64748B" };

const rows = [
  ["Main Gate", "ZG-A1", "ZKTeco", "Head Office", "STRCO India", "Online", "2026-09-28T09:42:00", "192.168.1.21", "Main gate biometric device at head office entrance."],
  ["Plant 1 Entry", "ES-B2", "eSSL", "Plant 1", "STRCO Manufacturing", "Online", "2026-09-28T09:40:00", "192.168.2.11", ""],
  ["Plant 1 Exit", "ES-B3", "eSSL", "Plant 1", "STRCO Manufacturing", "Offline", "2026-09-27T18:15:00", "192.168.2.12", ""],
  ["Warehouse", "ZK-W1", "ZKTeco", "Warehouse", "STRCO Logistics", "Online", "2026-09-28T09:38:00", "192.168.3.5", ""],
  ["Office Reception", "HF-R1", "Hikvision", "Reception", "", "Online", "2026-09-28T09:35:00", "192.168.1.30", ""],
  ["Security Cabin", "ES-S1", "eSSL", "Security", "", "Online", "2026-09-28T09:20:00", "192.168.1.44", ""],
];

export const organisationDevices = rows.map(
  ([name, code, type, location, company, status, lastSync, ipAddress, description], index) => ({
    id: index + 1,
    name,
    code,
    type,
    mode: "Bi-Directional",
    location,
    company,
    status,
    lastSync,
    ipAddress,
    port: "4370",
    serialNumber: "SN" + String(100200 + index * 37),
    description,
  }),
);

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const two = (value) => String(value).padStart(2, "0");

// "2026-09-28T09:42:00" -> { date: "28 Sep 2026", time: "09:42 AM" }
export const formatSync = (value) => {
  if (!value) return { date: "—", time: "" };
  const at = new Date(value);
  const hours = at.getHours();
  return {
    date: two(at.getDate()) + " " + MONTHS[at.getMonth()] + " " + at.getFullYear(),
    time: two(hours % 12 || 12) + ":" + two(at.getMinutes()) + (hours < 12 ? " AM" : " PM"),
  };
};

// ---- Add Device panel -------------------------------------------------

// How the device is used here. `value` is what the device record stores
// ("In Only", "Out Only" or "Bi-Directional").
export const NEW_DEVICE_MODES = [
  { value: "In Only", title: "In Device", text: "Records only IN punches" },
  { value: "Out Only", title: "Out Device", text: "Records only OUT punches" },
  { value: "Bi-Directional", title: "Bidirectional", text: "Records both IN and OUT punches" },
];

export const DEVICE_PURPOSES = [
  { value: "Attendance", title: "Attendance Device", text: "Used for employee attendance punches" },
  { value: "Patrolling", title: "Patrolling Device", text: "Used for security patrol rounds and checkpoints" },
];

export const emptyNewDevice = () => ({
  deviceId: "",
  // The lookup holds its status and the device information returned by the API.
  lookup: null,
  // Only device information fetched from the server can supply the mode.
  mode: "",
  name: "",
  purpose: "Attendance",
  location: "",
});

const sameCode = (a, b) =>
  String(a).trim().toLowerCase() === String(b).trim().toLowerCase();

// Checked when Fetch is pressed: the ID must be filled and not already added.
export const validateDeviceId = (deviceId, devices = []) => {
  if (!String(deviceId ?? "").trim()) return "Device ID is required.";
  if (devices.some((device) => sameCode(device.code, deviceId))) {
    return "This device is already added to the organisation.";
  }
  return "";
};

// Rules for the fields shared by Add Device and Edit Device.
export const validateDeviceConfig = (draft) => {
  const errors = {};
  if (!NEW_DEVICE_MODES.some((option) => option.value === draft.mode)) {
    errors.mode = "Select a device mode.";
  }
  if (!String(draft.name ?? "").trim()) errors.name = "Device name is required.";
  if (!DEVICE_PURPOSES.some((option) => option.value === draft.purpose)) {
    errors.purpose = "Select a device type.";
  }
  return errors;
};

export const validateNewDevice = (draft, devices = [], { requireLookup = true } = {}) => {
  const errors = validateDeviceConfig(draft);
  const idError = validateDeviceId(draft.deviceId, devices);
  if (idError) errors.deviceId = idError;
  else if (requireLookup && draft.lookup?.status !== "found") {
    errors.deviceId = "Fetch the device information before saving.";
  }
  return errors;
};

// Edit Device: the same form, filled from an existing record. Active/
// Inactive is not part of this form — it only changes through Deactivate /
// Reactivate (OrganisationDevices.js), so it is never read from or written
// back into this draft.
export const deviceToDraft = (device) => ({
  id: device.id,
  deviceId: device.code,
  lookup: null,
  mode: device.mode,
  name: device.name,
  purpose: device.purpose || "Attendance",
  location: device.location || "",
});

export const applyDraft = (device, draft) => ({
  ...device,
  name: draft.name.trim(),
  mode: draft.mode,
  purpose: draft.purpose,
  location: draft.location,
});

// The list record created from a saved Add Device draft. New devices are
// always Active; there is no way to create one as Inactive.
export const newDeviceRecord = (draft, devices = []) => ({
  id: Math.max(0, ...devices.map((device) => device.id)) + 1,
  name: draft.name.trim(),
  code: draft.deviceId.trim().toUpperCase(),
  type: draft.lookup.type,
  mode: draft.mode,
  purpose: draft.purpose,
  location: draft.location,
  company: "",
  active: true,
  // A device that has never connected has no status to report yet.
  status: "Offline",
  lastSync: "",
  ipAddress: "",
  port: "",
  serialNumber: draft.deviceId.trim().toUpperCase(),
  description: "",
});
